import React, { useEffect, useState } from 'react';
import ResponsiveTable from './ui/ResponsiveTable';
import { useUser } from '@clerk/clerk-react';
import SharedHeaderContractor from './SharedHeaderContractor';
import { useApi } from '../lib/useApi';

interface ContractorPaymentsProps {
  onNavigate: (screen: string) => void;
}

interface PaymentItem {
  id: string;
  projectId: string;
  projectAddress?: string;
  milestone: string;
  amountCents: number;
  createdAt: string;
  status: 'PENDING' | 'RELEASED' | 'FAILED' | 'REFUNDED';
  stripeTransferId?: string | null;
}

const ContractorPayments: React.FC<ContractorPaymentsProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const { get } = useApi();
  const [items, setItems] = useState<PaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stripeLinkLoading, setStripeLinkLoading] = useState(false);

  useEffect(() => {
    const fetchRemote = async () => {
      setLoading(true);
      try {
        if (!user?.id) return;
        const data = await get<{ items?: PaymentItem[] }>(`/api/contractors/${encodeURIComponent(user.id)}/payments`);
        const remote = Array.isArray(data?.items) ? data.items : [];
        // Merge in any local demo payments
        let local: any[] = [];
        try { local = JSON.parse(localStorage.getItem('demo_payments') || '[]'); } catch {}
        setItems([ ...remote, ...local ]);
      } catch {
        // 401/404/network — leave items as-is
      }
      setLoading(false);
    };
    fetchRemote();
    const onLocal = () => fetchRemote();
    window.addEventListener('demo:payment_submitted', onLocal as EventListener);
    return () => window.removeEventListener('demo:payment_submitted', onLocal as EventListener);
  }, [user?.id]);

  return (
    <div className="container">
      <SharedHeaderContractor onNavigate={onNavigate} />
      <div className="content">
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ margin: '16px 0 20px' }}>Payments History</h2>
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ color: 'black' }}
              disabled={stripeLinkLoading}
              onClick={async () => {
                setStripeLinkLoading(true);
                try {
                  const data = await get<{ url: string | null; needsOnboarding?: boolean; error?: string }>('/api/connect/login-link');
                  if (data?.url) {
                    if (data.needsOnboarding) {
                      // Not finished onboarding — open the onboarding flow
                      window.open(data.url, '_blank', 'noopener,noreferrer');
                    } else {
                      window.open(data.url, '_blank', 'noopener,noreferrer');
                    }
                  } else if (data?.error === 'No Stripe account linked') {
                    window.alert('No Stripe account found. Go to your dashboard and complete Stripe Connect setup first.');
                  } else if (data?.error) {
                    window.alert(data.error);
                  }
                } catch (e) {
                  window.alert('Could not reach the server. Make sure you are signed in and try again.');
                } finally {
                  setStripeLinkLoading(false);
                }
              }}
            >
              {stripeLinkLoading ? 'Opening…' : 'Set Up / Open Stripe Account'}
            </button>
          </div>
          {loading ? (
            <div>Loading...</div>
          ) : items.length === 0 ? (
            <div className="dashboard-card">No payments yet.</div>
          ) : (
            <div className="dashboard-card">
              <ResponsiveTable
                columns={[
                  { key: 'createdAt', header: 'Date', render: (p: PaymentItem) => new Date(p.createdAt).toLocaleDateString() },
                  { key: 'project', header: 'Project', render: (p: PaymentItem) => p.projectAddress || p.projectId },
                  { key: 'milestone', header: 'Milestone', render: (p: PaymentItem) => {
                    const labels: Record<string, string> = { M1_START: 'Project Start', M2_DEMO: 'Demolition', M3_COMPLETE: 'Project Complete' };
                    return labels[p.milestone] ?? p.milestone;
                  }},
                  { key: 'amountCents', header: 'Amount', render: (p: PaymentItem) => (p.amountCents/100).toLocaleString(undefined, { style: 'currency', currency: 'USD' }), className: 'text-right' },
                  { key: 'status', header: 'Status', render: (p: PaymentItem) => (
                    <span style={{ color: '#15803d', fontWeight: 600 }}>
                      ✓ Paid{p.stripeTransferId ? <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6, fontSize: 12, fontFamily: 'monospace' }}>{p.stripeTransferId}</span> : null}
                    </span>
                  )},
                ]}
                rows={items}
                rowKey={(p: PaymentItem) => p.id}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractorPayments;


